import { Button } from "@/design-system/components/ui";

// Partner Brand Ownership — the pagination footer for the three prequalification
// lists (D4).
//
// Built LOCALLY rather than importing the staff `components/layout/Pagination`.
// That component is identical in shape today and the temptation to reach for it
// is exactly the problem: the portal isolation rule exists so a staff-side
// refactor cannot silently repaint a partner's screen, and a shared list footer
// is the single most likely place for that to happen unnoticed. Design-system
// kit only, portal-local file, same visual language.
//
// Deliberately dumber than its staff counterpart in one way: the host passes
// only `totalCount` and this derives the page count, so a caller cannot hand in
// a `totalPages` that disagrees with the row count it is captioning.

export interface PrequalPagerProps {
  /** 1-based, as the server returns it. */
  page: number;
  pageSize: number;
  totalCount: number;
  /** The host owns the page state and re-fetches. */
  onPage: (next: number) => void;
}

export function PrequalPager({ page, pageSize, totalCount, onPage }: PrequalPagerProps) {
  // A single page needs no footer. "Showing 1–3 of 3 · Page 1 of 1" beside two
  // permanently-disabled buttons is noise on the overwhelmingly common case —
  // a partner with a handful of leads.
  if (totalCount <= pageSize) return null;

  // Guard the divisor rather than trusting the caller: a zero page size would
  // otherwise produce Infinity pages and a permanently-enabled Next.
  const size = Math.max(1, pageSize);
  const totalPages = Math.max(1, Math.ceil(totalCount / size));
  const from = (page - 1) * size + 1;
  const to = Math.min(page * size, totalCount);

  return (
    <div
      className="mt-space-3 flex flex-wrap items-center justify-between gap-space-2"
      data-testid="prequal-pager"
    >
      <span className="text-label text-gray-mid">
        Showing {from}–{to} of {totalCount} · Page {page} of {totalPages}
      </span>
      <div className="flex gap-space-2">
        <Button
          type="button"
          variant="secondary"
          size="sm"
          disabled={page <= 1}
          onClick={() => onPage(page - 1)}
          data-testid="prequal-pager-prev"
        >
          Previous
        </Button>
        <Button
          type="button"
          variant="secondary"
          size="sm"
          disabled={page >= totalPages}
          onClick={() => onPage(page + 1)}
          data-testid="prequal-pager-next"
        >
          Next
        </Button>
      </div>
    </div>
  );
}
